import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Autocomplete,
  TextField,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  InputAdornment,
  CircularProgress,
  Typography,
  Paper
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ScienceIcon from '@mui/icons-material/Science';
import { Virtuoso } from 'react-virtuoso';
import {
  useCompaniesQuery,
  useCompanyEquipmentQuery,
  type EquipmentSummary
} from '@api/bff/equipment/useEquipment';
import { EquipmentCard } from './EquipmentCard';

type Props = {
  open: boolean;
  defaultCompanyName?: string | null;
  selectedGlobalId?: string | null;
  onClose: () => void;
  onSelect: (equipment: EquipmentSummary) => void;
};

export const EquipmentPickerDialog = ({
  open,
  defaultCompanyName = null,
  selectedGlobalId = null,
  onClose,
  onSelect
}: Props) => {
  const [companyName, setCompanyName] = useState<string | null>(
    defaultCompanyName
  );
  const [companyInput, setCompanyInput] = useState(defaultCompanyName ?? '');
  const [debouncedCompany, setDebouncedCompany] = useState('');
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (open) {
      setCompanyName(defaultCompanyName);
      setCompanyInput(defaultCompanyName ?? '');
      setSearch('');
    }
  }, [open, defaultCompanyName]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedCompany(companyInput), 300);
    return () => clearTimeout(timer);
  }, [companyInput]);

  const { data: companies = [], isFetching: isLoadingCompanies } =
    useCompaniesQuery(debouncedCompany, open);
  const { data: equipment = [], isFetching: isLoadingEquipment } =
    useCompanyEquipmentQuery(companyName, open);

  const companyOptions = useMemo(
    () =>
      companyName && !companies.includes(companyName)
        ? [companyName, ...companies]
        : companies,
    [companies, companyName]
  );

  const filteredEquipment = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return equipment;
    return equipment.filter(
      e =>
        e.id.toLowerCase().includes(term) ||
        (e.serialNumber?.toLowerCase().includes(term) ?? false) ||
        (e.description?.toLowerCase().includes(term) ?? false) ||
        (e.manufacturer?.toLowerCase().includes(term) ?? false) ||
        (e.modelNumber?.toLowerCase().includes(term) ?? false)
    );
  }, [equipment, search]);

  const handleCompanyChange = useCallback(
    (_event: unknown, next: string | null) => {
      setCompanyName(next);
      setSearch('');
    },
    []
  );

  const handleSelect = useCallback(
    (item: EquipmentSummary) => {
      onSelect(item);
      onClose();
    },
    [onSelect, onClose]
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Select Equipment</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Paper
            elevation={0}
            sx={theme => ({
              p: 2,
              border: `1px solid ${theme.palette.divider}`,
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5
            })}
          >
            <Autocomplete
              options={companyOptions}
              value={companyName}
              inputValue={companyInput}
              onInputChange={(_e, next) => setCompanyInput(next)}
              onChange={handleCompanyChange}
              filterOptions={x => x}
              isOptionEqualToValue={(option, val) => option === val}
              loading={isLoadingCompanies}
              noOptionsText="No companies found"
              size="small"
              renderInput={params => (
                <TextField {...params} label="Company" />
              )}
            />
            <TextField
              placeholder="Search by ID, serial, description..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              fullWidth
              size="small"
              disabled={!companyName}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon
                      sx={{ fontSize: 20, color: 'text.secondary' }}
                    />
                  </InputAdornment>
                )
              }}
            />
          </Paper>

          {!companyName && (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <Typography variant="body2" color="text.secondary">
                Select a company to view its equipment.
              </Typography>
            </Box>
          )}

          {companyName && isLoadingEquipment && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 1.5,
                py: 6
              }}
            >
              <CircularProgress size={32} />
              <Typography variant="body2" color="text.secondary">
                Loading equipment...
              </Typography>
            </Box>
          )}

          {companyName &&
            !isLoadingEquipment &&
            filteredEquipment.length === 0 && (
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <ScienceIcon
                  sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }}
                />
                <Typography variant="body1" color="text.secondary">
                  No equipment found
                </Typography>
              </Box>
            )}

          {companyName && !isLoadingEquipment && filteredEquipment.length > 0 && (
            <Virtuoso
              style={{ height: 420 }}
              data={filteredEquipment}
              overscan={200}
              itemContent={(_index, item) => (
                <Box sx={{ mb: 1.5 }}>
                  <EquipmentCard
                    equipment={item}
                    isSelected={item.globalId === selectedGlobalId}
                    onSelect={handleSelect}
                  />
                </Box>
              )}
            />
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
};
